import React, { useState } from 'react';
import { 
  Drawer, 
  List, 
  ListItemIcon, 
  ListItemText, 
  ListItemButton,
  Toolbar,
  Collapse,
  Typography,
  Box
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { featureStructure, ParentFeature } from '../model/pageList';

interface MenuDrawerProps {
    drawerWidth: number;
    drawerOpen: boolean;
    setCurrentView: (view: string) => void;
}

const MenuDrawer: React.FC<MenuDrawerProps> = (props) => {
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [selectedItem, setSelectedItem] = useState<string>('');

    const handleParentClick = (parentKey: string, parentPath: string) => {
        // Toggle expansion
        const isExpanded = expandedItems.includes(parentKey);
        if (isExpanded) {
            setExpandedItems(expandedItems.filter(item => item !== parentKey));
        } else {
            setExpandedItems([...expandedItems, parentKey]);
        }
        
        // Set current view to parent page
        props.setCurrentView(parentPath);
        setSelectedItem(parentKey);
    };

    const handleSubFeatureClick = (parentPath: string, tabKey: string, fullKey: string) => {
        props.setCurrentView(`${parentPath}#${tabKey}`);
        setSelectedItem(fullKey);
    };

    return (
        <Drawer
            variant='persistent'
            anchor='left'
            open={props.drawerOpen}
            sx={{
                width: props.drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: props.drawerWidth,
                    boxSizing: 'border-box',
                },
            }}
        >
            <Toolbar />
            <Box sx={{ overflow: 'auto' }}>
                <List sx={{ pt: 1 }}>
                    {featureStructure.map((parentFeature: ParentFeature) => (
                        <React.Fragment key={parentFeature.key}>
                            {/* Parent Feature Item */}
                            <ListItemButton
                                onClick={() => handleParentClick(parentFeature.key, parentFeature.path)}
                                selected={selectedItem === parentFeature.key}
                                sx={{
                                    py: 1.5,
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.light',
                                        '&:hover': {
                                            backgroundColor: 'primary.light',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: 'inherit' }}>
                                    {parentFeature.icon}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={
                                        <Typography variant="subtitle2" fontWeight="medium">
                                            {parentFeature.label}
                                        </Typography>
                                    }
                                />
                                {expandedItems.includes(parentFeature.key) ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>

                            {/* Sub Features */}
                            <Collapse 
                                in={expandedItems.includes(parentFeature.key)} 
                                timeout="auto" 
                                unmountOnExit
                            >
                                <List component="div" disablePadding>
                                    {parentFeature.subFeatures.map((subFeature) => {
                                        const fullKey = `${parentFeature.key}-${subFeature.key}`;
                                        return (
                                            <ListItemButton
                                                key={fullKey}
                                                onClick={() => handleSubFeatureClick(parentFeature.path, subFeature.tabKey, fullKey)}
                                                selected={selectedItem === fullKey}
                                                sx={{
                                                    pl: 4,
                                                    py: 1,
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'secondary.light',
                                                        '&:hover': {
                                                            backgroundColor: 'secondary.light',
                                                        },
                                                    },
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                                                    {subFeature.icon}
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={
                                                        <Typography variant="body2">
                                                            {subFeature.label}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    ))}
                </List>
            </Box>
        </Drawer>
    );
};

export default MenuDrawer;